import React, { useState } from 'react';
import Pagination from '../../Common/Pagination';
import { CSVLink } from "react-csv";
import useExecFunctionOnBackButton from '../../Common/useExecFunctionOnBackButton'
import SectionHeader, { SectionHeaderTypes } from '../../Common/SectionHeader';

export default function SiteLotStabilizationLogEntries({ logTypeName, logs, onPrint, onCloseLogEntries }) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', { day: "2-digit", month: "2-digit", year: "numeric" });
    const [selectedPage, setSelectedPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortedBy, setSortedBy] = useState('lot');
    const [sortOrder, setSortOrder] = useState('asc');

    useExecFunctionOnBackButton(() => {
        window.history.go(1);
        onCloseLogEntries();
    });

    function handleSortByAndOrder(sortByAndOrder) {
        const [sortByParm, sortOrderParam] = sortByAndOrder.split("-");

        setSortOrder(sortOrderParam);
        setSortedBy(sortByParm);
    }

    function handleSortBy(sortBy) {
        let newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';

        setSortOrder(sortedBy === sortBy ? newSortOrder : 'desc');
        setSortedBy(sortBy);
    }

    function getSortComparer() {
        let isDesc = sortOrder === "desc";
        let compareFn = undefined;
        switch (sortedBy?.toLowerCase()) {
            case 'id':
                compareFn = (a, b) => (isDesc ? b.id - a.id : a.id - b.id);
                break;
            case 'comments':
                compareFn = (a, b) => (isDesc ? ('' + b.comments).localeCompare(a.comments) : ('' + a.comments).localeCompare(b.comments));
                break;
            case 'tempstabilization':
                compareFn = (a, b) => {
                    const timeA = new Date(a.tempStabilizationDate).getTime();
                    const timeB = new Date(b.tempStabilizationDate).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'finalstabilization':
                compareFn = (a, b) => {
                    const timeA = new Date(a.finalStabilizationDate).getTime();
                    const timeB = new Date(b.finalStabilizationDate).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'construction':
                compareFn = (a, b) => {
                    const timeA = new Date(a.activeConstructionDate).getTime();
                    const timeB = new Date(b.activeConstructionDate).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'ownership':
                compareFn = (a, b) => {
                    const timeA = new Date(a.ownershipDate).getTime();
                    const timeB = new Date(b.ownershipDate).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'lot':
            default:
                compareFn = (a, b) => (isDesc ? ('' + b.lotNumber).localeCompare(a.lotNumber) : ('' + a.lotNumber).localeCompare(b.lotNumber));
                break;
        }

        return compareFn;
    }

    function handleSelectPage(pageNum) {
        setSelectedPage(pageNum);
    }

    let shownStartIndex = (pageSize * selectedPage) - pageSize;
    let shownEndIndex = (pageSize * selectedPage);

    let csvHeaders = [
        { label: "Lot Number", key: "lotNumber" },
        { label: "Date of Temporary Stabilization", key: "tempStabilizationDate" },
        { label: "Date of Active Construction", key: "activeConstructionDate" },
        { label: "Final Stabilization Date", key: "finalStabilizationDate" },
        { label: "Date of Ownership", key: "ownershipDate" },
        { label: "Comments", key: "comments" },
    ];

    let compareFn = getSortComparer(sortedBy, sortOrder);
    let sortedLogs = (logs && logs.toSorted(compareFn)) || [];

    let csvData = sortedLogs && sortedLogs.map(log => ({
        lotNumber: log.lotNumber,
        tempStabilizationDate: log.tempStabilizationDate ? dateFormatter.format(new Date(log.tempStabilizationDate)) : "",
        activeConstructionDate: log.activeConstructionDate ? dateFormatter.format(new Date(log.activeConstructionDate)) : "",
        finalStabilizationDate: log.finalStabilizationDate ? dateFormatter.format(new Date(log.finalStabilizationDate)) : "",
        ownershipDate: log.ownershipDate ? dateFormatter.format(new Date(log.ownershipDate)) : "",
        comments: log.comments
    }));

    let logListItems = sortedLogs && sortedLogs
        .map((log, index) => <tr className={index < shownStartIndex || index >= shownEndIndex ? "hidden" : ""} key={log.id}>
            <td>{log.lotNumber}</td>
            <td className="tabular-nums">{log.tempStabilizationDate && dateFormatter.format(new Date(log.tempStabilizationDate))}</td>
            <td className="tabular-nums">{log.activeConstructionDate && dateFormatter.format(new Date(log.activeConstructionDate))}</td>
            <td className="tabular-nums">{log.finalStabilizationDate && dateFormatter.format(new Date(log.finalStabilizationDate))}</td>
            <td className="tabular-nums">{log.ownershipDate && dateFormatter.format(new Date(log.ownershipDate))}</td>
            <td>{log.comments}</td>
            <td className="smallScreen">
                <div className="log">
                    {log.lotNumber && (<div>
                        <span className="fieldDesc">Lot Number:</span>
                        {log.lotNumber}
                    </div>)}
                    {log.tempStabilizationDate && (<div>
                        <span className="fieldDesc">Temporary Stabilization On:</span>
                        {log.tempStabilizationDate && dateFormatter.format(new Date(log.tempStabilizationDate))}
                    </div>)}
                    {log.activeConstructionDate && (<div>
                        <span className="fieldDesc">Active Construction On:</span>
                        {log.activeConstructionDate && dateFormatter.format(new Date(log.activeConstructionDate))}
                    </div>)}
                    {log.finalStabilizationDate && (<div>
                        <span className="fieldDesc">Final Stabilization On:</span>
                        {log.finalStabilizationDate && dateFormatter.format(new Date(log.finalStabilizationDate))}
                    </div>)}
                    {log.ownershipDate && (<div>
                        <span className="fieldDesc">Ownership Date:</span>
                        {log.ownershipDate && dateFormatter.format(new Date(log.ownershipDate))}
                    </div>)}
                    {log.comments && (<div>
                        <span className="fieldDesc">Comments:</span>
                        <div className="desc">{log.comments}</div>
                    </div>)}
                </div>
            </td>
        </tr>);

    if (selectedPage > 1 && (pageSize * selectedPage) - logListItems.length > 0) {
        let emptyRows = Array.from(
            { length: (pageSize * selectedPage) - logListItems.length },
            (cv, i) => (<tr className="noBkg noPrint" key={`R${i}`}><td>&nbsp;</td></tr>));
        logListItems = logListItems.concat(emptyRows);
    }

    let printButton = (<button onClick={onPrint}>Print</button>);
    let exportButton = (<CSVLink data={csvData} headers={csvHeaders} filename={`${logTypeName}.csv`} className="button" target="_blank">Export</CSVLink>);

    let sortSelect = (<select className="bg-drhPalette-secondary " onChange={(e) => handleSortByAndOrder(e.target.value)} value={`${sortedBy}-${sortOrder}`}>
        <option className="checked:font-semibold checked:text-white" value='lot-desc'>Lot Number desc</option>
        <option className="checked:font-semibold checked:text-white" value='lot-asc'>Lot Number asc</option>
        <option className="checked:font-semibold checked:text-white" value='tempstabilization-desc'>Temp Stabilization desc</option>
        <option className="checked:font-semibold checked:text-white" value='tempstabilization-asc'>Temp Stabilization asc</option>
        <option className="checked:font-semibold checked:text-white" value='construction-desc'>Construction Date desc</option>
        <option className="checked:font-semibold checked:text-white" value='construction-asc'>Construction Date asc</option>
        <option className="checked:font-semibold checked:text-white" value='finalstabilization-desc'>Final Stabilization desc</option>
        <option className="checked:font-semibold checked:text-white" value='finalstabilization-asc'>Final Stabilization asc</option>
        <option className="checked:font-semibold checked:text-white" value='ownership-desc'>Ownership Date desc</option>
        <option className="checked:font-semibold checked:text-white" value='ownership-asc'>Ownership Date asc</option>
        <option className="checked:font-semibold checked:text-white" value='comments-desc'>Comments desc</option>
        <option className="checked:font-semibold checked:text-white" value='comments-asc'>Comments asc</option>
    </select>);

    let sortBarSmallScreen = (<SectionHeader classes="selectAllSmallScreen m-1 pl-0.5 justify-end md:hidden" sectionHeaderType={SectionHeaderTypes.Secondary}>
        <div className="text-base">
            <span className="hidden xsm:inline">Sort by</span>
            {sortSelect}
        </div>
    </SectionHeader>);

    let sortIconClass = sortOrder === 'desc' ? 'dhicon-down-open' : 'dhicon-up-open';

    //let sortableIcon = (<span className="flex"><i className="dhicon-up-open relative -top-1" /><i className="dhicon-down-open relative top-[3px] -left-[22.5px]" /></span>);
    //let sortableIcon2 = (<span className="flex"><i className="dhicon-up-open relative top-2" /><i className="dhicon-down-open relative top-4 -left-[22.5px]" /></span>);
    //let sortableColDescClass = "relative -left-[22px]";
    let sortableIcon = (<div className="flex"><i className="iconUpDown" /></div>);
    let sortableIcon2 = (<div className="flex"><i className="iconUpDown" /></div>);
    let sortableColDescClass = "";

    let lotHeader = (<th onClick={() => handleSortBy('lot')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'lot' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortedBy !== 'lot' ? sortableColDescClass : ''}>Lot Number</span>
        </div>
    </th>);

    let commentsHeader = (<th onClick={() => handleSortBy('comments')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'comments' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortedBy !== 'comments' ? sortableColDescClass : ''}>Comments</span>
        </div>
    </th>);

    let tempStabilizationHeader = (<th onClick={() => handleSortBy('tempstabilization')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'tempstabilization' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'tempstabilization' ? sortableColDescClass : ''}>Date of Temporary</span>
                <br />
                <span className={sortedBy !== 'tempstabilization' ? sortableColDescClass : ''}>Stabilization</span></div>
        </div>
    </th>);

    let constructionHeader = (<th onClick={() => handleSortBy('construction')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'construction' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'construction' ? sortableColDescClass : ''}>Date of Active</span>
                <br />
                <span className={sortedBy !== 'construction' ? sortableColDescClass : ''}>Construction</span></div>
        </div>
    </th>);

    let finalStabilizationHeader = (<th onClick={() => handleSortBy('finalstabilization')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'finalstabilization' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'finalstabilization' ? sortableColDescClass : ''}>Final Stabilization</span>
                <br />
                <span className={sortedBy !== 'finalstabilization' ? sortableColDescClass : ''}>Date</span></div>
        </div>
    </th>);

    let ownershipHeader = (<th onClick={() => handleSortBy('ownership')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'ownership' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'ownership' ? sortableColDescClass : ''}>Date of</span>
                <br />
                <span className={sortedBy !== 'ownership' ? sortableColDescClass : ''}>Ownership</span></div>
        </div>
    </th>);

    let logList = logListItems.length === 0 ? undefined : (<>
        {sortBarSmallScreen}
        <table id="logEntries" className="itemList">
            <thead>
                <tr>
                    {lotHeader}
                    {tempStabilizationHeader}
                    {constructionHeader}
                    {finalStabilizationHeader}
                    {ownershipHeader}
                    {commentsHeader}
                </tr>
            </thead>
            <tbody>{logListItems}</tbody>
        </table>
        <Pagination itemCount={logs.length} pageSize={pageSize} selectedPage={selectedPage} hideWhenSinglePage={false} onSelectPage={handleSelectPage}>
            <div className="flex gap-x-1">
            </div>
        </Pagination>
    </>);

    let logTypeNameDescription = (<div className="logType">
        <div className="flexbox-flex align-start overflow-x-hidden">
             <span className="nameContainer">
                <span className="name">Log Entries - {logTypeName}</span>
            </span>
        </div>
     </div>);

    return (
        <div className="logTypeContainer">
            {logTypeNameDescription}
            {logList}
        </div>
    );
}